import type { PaginatedResponse, UserRef } from '@/types'

export type TransactionType =
  | 'topup'
  | 'lesson_payment'
  | 'subscription_purchase'
  | 'correction'
  | 'refund'

export type TransactionDirection = 'credit' | 'debit'

export type TransactionSource = 'teacher' | 'student_pending' | 'admin' | 'system'

export type TransactionStatus = 'pending' | 'approved' | 'rejected'

export type PriceSource =
  | 'subscription'
  | 'contract'
  | 'teacher_default'
  | 'manual'
  | 'free'

export type LessonPaymentStatus = 'not_required' | 'pending' | 'paid' | 'deferred'

export type LessonPaymentMethod =
  | 'subscription'
  | 'from_balance'
  | 'cash'
  | 'deferred'
  | 'none'

export type LessonSubscriptionType = 'individual' | 'group'

export type LessonSubscriptionGrantStatus = 'active' | 'expired' | 'exhausted' | 'canceled'

/** Вычисляемое состояние grant для UI: статус + «ожидает начала» для активного с будущим starts_at */
export type LessonSubscriptionGrantDisplayState = LessonSubscriptionGrantStatus | 'pending_start'

export interface ContractBalance {
  contract_id: number
  balance_amount: string
  currency: string
  updated_at?: string | null
  settings: {
    payment_notifications_enabled: boolean
  }
}

export interface ContractTransaction {
  id: number
  contract_id: number
  type: TransactionType
  direction: TransactionDirection
  amount: string
  currency: string
  source: TransactionSource
  status: TransactionStatus
  description?: string | null
  meta?: Record<string, unknown> | null
  created_by?: number | null
  approved_by?: number | null
  approved_at?: string | null
  created_at?: string | null
}

export interface ContractTransactionFilters {
  type?: TransactionType
  direction?: TransactionDirection
  status?: TransactionStatus
  from?: string
  to?: string
  page?: number
  per_page?: number
}

export interface TeacherLessonPrice {
  id: number
  teacher_id: number
  price_per_hour: string | number
  currency: string
  effective_from: string
  created_by?: number | null
  created_at?: string | null
}

export interface ContractLessonPrice {
  id: number
  contract_id: number
  price_per_hour: string | number
  currency: string
  effective_from: string
  created_by?: number | null
  created_at?: string | null
}

export interface LessonPriceCalculation {
  price_amount: number | null
  currency: string
  source: PriceSource
  source_meta?: {
    grant_id: number
    plan_title: string | null
    lessons_total: number
    lessons_remaining: number
    next_number: number
    covered_count?: number
    pending_count?: number
    participants_total?: number
  } | null
}

/** Данные «N из M занятие по абонементу» в LessonResource (при subscription_id) */
export interface LessonSubscriptionMeta {
  plan_title: string | null
  lessons_total: number
  consumption_number: number
}

/** Сводка финансов договора в списке учеников учителя */
export interface ContractFinanceSummary {
  active_grant: {
    plan_title: string | null
    lessons_remaining: number
    lessons_total: number
  } | null
  balance_amount: string
}

export interface LessonSubscriptionPlan {
  id: number
  teacher_id: number
  title: string
  description?: string | null
  subscription_type: LessonSubscriptionType
  lessons_count: number
  duration_days: number
  price_per_lesson: string | number
  total_price?: string | number
  currency: string
  min_group_size?: number | null
  max_group_size?: number | null
  is_active: boolean
  created_at?: string | null
  updated_at?: string | null
}

export interface LessonSubscriptionGrant {
  id: number
  plan_id: number
  contract_id: number
  group_id?: number | null
  batch_id?: string | null
  starts_at: string
  expires_at?: string | null
  lessons_total: number
  lessons_remaining: number
  status: LessonSubscriptionGrantStatus
  payment_method?: LessonPaymentMethod | null
  display_state: LessonSubscriptionGrantDisplayState
  canceled_at?: string | null
  canceled_reason?: string | null
  created_by?: number | null
  plan?: LessonSubscriptionPlan
  contract?: {
    id: number
    student_id?: number
    student?: UserRef
  }
}

/** Списание занятия с абонемента (consumption) */
export interface LessonSubscriptionConsumption {
  id: number
  grant_id: number
  lesson_id: number | null
  consumed_at?: string | null
  reverted_at?: string | null
  lesson?: {
    id: number
    status?: string
    planned_start_at?: string | null
  } | null
}

export interface GroupSubscriptionBatch {
  batch_id: string
  starts_at?: string | null
  expires_at?: string | null
  plan?: LessonSubscriptionPlan | null
  participants_count: number
  grants?: LessonSubscriptionGrant[]
}

export interface LessonSubscriptionGrantDistribution {
  id: number
  group_grant_batch_id: string
  grant_id: number
  contract_id: number
  share_amount: string | number
  grant?: LessonSubscriptionGrant
}

export interface IndividualLessonPaymentState {
  payment_status: LessonPaymentStatus
  payment_method?: LessonPaymentMethod | null
  price_amount?: number | null
  price_currency?: string | null
  price_source?: PriceSource | null
  payment_finalized_at?: string | null
  finance_edit_window_until?: string | null
}

export interface GroupLessonPaymentEntry {
  student_id: number
  contract_id?: number | null
  payment_status: LessonPaymentStatus
  payment_method?: LessonPaymentMethod | null
  student?: UserRef
}

export interface GroupLessonPaymentState {
  payment_status: LessonPaymentStatus
  entries: GroupLessonPaymentEntry[]
  group_subscription_meta?: Record<string, unknown> | null
  finance_edit_window_until?: string | null
}

export type LessonPaymentState = IndividualLessonPaymentState | GroupLessonPaymentState

export type GroupSubscriptionBatchDetail = {
  batch_id: string
  starts_at?: string | null
  expires_at?: string | null
  plan?: LessonSubscriptionPlan | null
  grants: LessonSubscriptionGrant[]
  distributions: LessonSubscriptionGrantDistribution[]
}

export function isGroupPaymentState(
  state: LessonPaymentState
): state is GroupLessonPaymentState {
  return Array.isArray((state as GroupLessonPaymentState).entries)
}

export type ContractTransactionsPage = PaginatedResponse<ContractTransaction>
